import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { sb } from '../../supabase';
import { displayCategoryForCode } from '../../lib/factoryCategories';

// Admin view of every factory's contact channels, split into READY (a valid,
// unique, clean email — usable for the quote-request outreach) vs NEEDS ATTENTION
// (missing / malformed / placeholder-duplicate / hidden-character email). Row
// "Edit" opens the factory detail to fix it.
const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const validPhone = (v) => (v || '').replace(/[^\d]/g, '').length >= 7;
// invisible / bidi / zero-width chars that silently corrupt a value
const hasHidden = (v) => /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/.test(v || '');

const RED = '#c0392b';
const tdS = { padding: '10px 12px', verticalAlign: 'top' };

function Th({ children, isAr }) {
  return <th style={{ padding: '10px 12px', fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', textAlign: isAr ? 'right' : 'left', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Stat({ n, label, tone }) {
  const bad = tone === 'bad'; const ok = tone === 'ok';
  return (
    <div style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid var(--border-subtle)', background: bad ? 'rgba(192,57,43,0.06)' : ok ? 'rgba(45,106,79,0.07)' : 'var(--bg-raised)', minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: bad ? RED : ok ? '#2D6A4F' : 'var(--text-primary)' }}>{n}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{label}</div>
    </div>
  );
}
function ReasonChip({ reason, isAr }) {
  const M = {
    noEmail: { ar: 'بلا إيميل', en: 'No email' },
    format: { ar: 'صيغة غير صحيحة', en: 'Invalid format' },
    hidden: { ar: 'حرف مخفي', en: 'Hidden character' },
    dup: { ar: 'إيميل مكرّر', en: 'Duplicate email' },
  };
  const t = M[reason]; if (!t) return null;
  return <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, color: RED, background: 'rgba(192,57,43,0.10)', border: '1px solid rgba(192,57,43,0.30)', borderRadius: 999, padding: '2px 9px' }}>{isAr ? t.ar : t.en}</span>;
}

export default function ConsoleFactoryContacts({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('attn'); // 'attn' | 'ready' | 'all'
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };

  const [taggedOnly, setTaggedOnly] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [tagErr, setTagErr] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await sb.from('factory_directory')
        .select('id, company_name, company_name_latin, category, email, phone, wechat, website, city, is_active, wa_phone_e164, wa_opt_in_at, wa_opt_out_at, wa_opt_in_source')
        .order('company_name_latin', { ascending: true });
      if (!alive) return;
      if (error) { setErr(error.message); setRows([]); return; }
      setRows(data || []);
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  // Consent is written by a SECURITY DEFINER RPC, never by this client: the
  // server stamps who tagged it and when, and untagging can only clear our own
  // manual note — it can never erase an opt-out or a reply-based consent.
  const toggleTag = async (f) => {
    setBusyId(f.id); setTagErr('');
    const fn = f.waTagged ? 'admin_unmark_factory_prior_contact' : 'admin_mark_factory_prior_contact';
    const { error } = await sb.rpc(fn, { p_factory_id: f.id });
    if (error) setTagErr(error.message);
    else setReloadKey((k) => k + 1);
    setBusyId('');
  };

  const enriched = useMemo(() => {
    const list = rows || [];
    // count each email (normalized) to catch placeholders/duplicates shared across factories
    const counts = {};
    list.forEach((f) => { const e = nf(f.email).toLowerCase(); if (e) counts[e] = (counts[e] || 0) + 1; });
    return list.map((f) => {
      const email = nf(f.email);
      const phone = nf(f.phone);
      let reason = null;
      if (!email) reason = 'noEmail';
      else if (hasHidden(email)) reason = 'hidden';
      else if (!validEmail(email)) reason = 'format';
      else if (counts[email.toLowerCase()] > 1) reason = 'dup';
      // Effective consent, same rule the sender enforces: an opt-in that a later
      // opt-out has not superseded.
      const optIn = f.wa_opt_in_at ? new Date(f.wa_opt_in_at).getTime() : 0;
      const optOut = f.wa_opt_out_at ? new Date(f.wa_opt_out_at).getTime() : 0;
      const waConsent = optIn > 0 && (optOut === 0 || optOut < optIn);
      return {
        ...f, email, phone, phoneOk: validPhone(phone), reason, ready: reason === null,
        waConsent,
        waTagged: waConsent && f.wa_opt_in_source === 'prior_contact',
        waSendable: !!f.wa_phone_e164,
      };
    });
  }, [rows]);

  const counts = useMemo(() => ({
    total: enriched.length,
    ready: enriched.filter((f) => f.ready).length,
    attn: enriched.filter((f) => !f.ready).length,
    tagged: enriched.filter((f) => f.waTagged).length,
    sendable: enriched.filter((f) => f.waSendable).length,
  }), [enriched]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (tab === 'ready') list = list.filter((f) => f.ready);
    else if (tab === 'attn') list = list.filter((f) => !f.ready);
    // Composes with the tabs above rather than replacing them: the email-quality
    // axis and the WhatsApp-consent axis are different questions.
    if (taggedOnly) list = list.filter((f) => f.waTagged);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((f) => [f.company_name, f.company_name_latin, f.email, f.phone, f.city]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    return list;
  }, [enriched, tab, q, taggedOnly]);

  const Seg = ({ k, label }) => (
    <button type="button" onClick={() => setTab(k)}
      className={`ac-chip${tab === k ? ' on' : ''}`} style={font}>{label}</button>
  );

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="factory-contacts">
      <div className="ac-page">
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1" style={font}>{isAr ? 'تواصل المصانع' : 'Factory Contacts'}</h1>
            <p className="ac-sub" style={font}>{isAr ? 'مقسّمة لفئتين: جاهزة (إيميل صحيح ومميّز) وتحتاج مراجعة (ناقص/غير صحيح/مكرّر/حرف مخفي).' : 'Split into Ready (valid, unique email) and Needs attention (missing/invalid/duplicate/hidden-character).'}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Stat n={counts.total} label={isAr ? 'إجمالي المصانع' : 'Factories'} />
          <Stat n={counts.ready} label={isAr ? 'جاهزة' : 'Ready'} tone="ok" />
          <Stat n={counts.attn} label={isAr ? 'تحتاج مراجعة' : 'Needs attention'} tone="bad" />
          <Stat n={counts.tagged} label={isAr ? 'موسوم للواتساب' : 'WhatsApp-tagged'} tone="ok" />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Seg k="attn" label={`${isAr ? 'تحتاج مراجعة' : 'Needs attention'} (${counts.attn})`} />
          <Seg k="ready" label={`${isAr ? 'جاهزة' : 'Ready'} (${counts.ready})`} />
          <Seg k="all" label={`${isAr ? 'الكل' : 'All'} (${counts.total})`} />
          <button type="button" onClick={() => setTaggedOnly((v) => !v)}
            className={`ac-chip${taggedOnly ? ' on' : ''}`} style={font}>
            {isAr ? `الموسومون فقط (${counts.tagged})` : `Tagged only (${counts.tagged})`}
          </button>
        </div>

        <p style={{ ...font, fontSize: 12.5, lineHeight: 1.8, color: 'var(--text-secondary)',
                    background: 'rgba(154,123,79,0.07)', border: '1px solid rgba(154,123,79,0.22)',
                    borderRadius: 10, padding: '10px 14px', margin: '0 0 14px', maxWidth: 760 }}>
          {isAr
            ? 'الوسم يسجّل أساس الإرسال — المصنع الموسوم سيستقبل رسائل مَعبر عند تفعيل القناة. لا ترسل الآن أي رسالة، لكنه ليس زرًّا بلا أثر.'
            : 'Tagging records the basis for messaging — a tagged factory will receive Maabar messages once the channel is switched on. It sends nothing now, but it is not a no-op.'}
        </p>
        {tagErr && <div className="ac-empty" style={{ ...font, color: RED }}>{tagErr}</div>}

        <div style={{ marginBottom: 14 }}>
          <input className="ac-input" style={{ width: '100%', maxWidth: 360, ...font }}
            placeholder={isAr ? 'ابحث باسم / إيميل / جوال…' : 'Search name / email / phone…'}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        {err && <div className="ac-empty" style={font}>{err}</div>}
        {rows === null ? (
          <div className="ac-empty" style={font}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</div>
        ) : filtered.length === 0 ? (
          <div className="ac-empty" style={font}>{isAr ? 'لا نتائج.' : 'No results.'}</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'var(--bg-raised)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, ...font }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)' }}>
                  <Th isAr={isAr}>{isAr ? 'المصنع' : 'Factory'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الفئة' : 'Category'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الإيميل' : 'Email'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الجوال' : 'Phone'}</Th>
                  <Th isAr={isAr}>WeChat</Th>
                  <Th isAr={isAr}>{isAr ? 'واتساب' : 'WhatsApp'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الحالة' : 'Status'}</Th>
                  <Th isAr={isAr}> </Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const name = nf(f.company_name_latin) || nf(f.company_name) || '—';
                  const cat = displayCategoryForCode(f.category);
                  const emailBad = !!f.reason;
                  return (
                    <tr key={f.id} style={{ borderTop: '1px solid var(--border-subtle)', background: f.ready ? 'transparent' : 'rgba(192,57,43,0.045)' }}>
                      <td style={tdS}>
                        <div style={{ fontWeight: 600 }} dir="ltr">{name}</div>
                        {nf(f.city) && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{f.city}</div>}
                        {!f.is_active && <span className="ac-badge warn" style={{ fontSize: 10 }}>{isAr ? 'مخفي' : 'Hidden'}</span>}
                      </td>
                      <td style={tdS}>{cat ? (cat.label[lang] || cat.label.en) : '—'}</td>
                      <td style={{ ...tdS, color: emailBad ? RED : 'var(--text-primary)', fontWeight: emailBad ? 600 : 400 }} dir="ltr">
                        {f.email || (isAr ? '— ناقص' : '— missing')}
                      </td>
                      <td style={{ ...tdS, color: f.phoneOk ? 'var(--text-primary)' : RED, fontWeight: f.phoneOk ? 400 : 600 }} dir="ltr">
                        {f.phone || (isAr ? '— ناقص' : '— missing')}
                      </td>
                      <td style={tdS} dir="ltr">{nf(f.wechat) || '—'}</td>
                      <td style={tdS}>
                        {f.waTagged ? (
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#2D6A4F', background: 'rgba(45,106,79,0.10)', border: '1px solid rgba(45,106,79,0.28)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>
                            {isAr ? '✓ سبق التواصل' : '✓ Contacted'}
                          </span>
                        ) : f.waConsent ? (
                          // Consent from a reply or the claim flow — a stronger basis
                          // than our own note, and the RPC refuses to overwrite it.
                          <span className="ac-badge" style={{ fontSize: 10, whiteSpace: 'nowrap' }} dir="ltr">
                            {f.wa_opt_in_source || 'opted in'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                        )}
                        <div style={{ marginTop: 6 }}>
                          <button type="button" className="ac-btn" disabled={busyId === f.id || (!f.waSendable && !f.waTagged) || (f.waConsent && !f.waTagged)}
                            title={!f.waSendable && !f.waTagged
                              ? (isAr ? 'لا يوجد رقم صالح للواتساب' : 'No WhatsApp-capable number')
                              : (f.waConsent && !f.waTagged ? (isAr ? 'موافقة من مصدر أقوى' : 'Consent from a stronger source') : '')}
                            onClick={() => toggleTag(f)}
                            style={{ padding: '4px 10px', fontSize: 11.5, opacity: (busyId === f.id || (!f.waSendable && !f.waTagged) || (f.waConsent && !f.waTagged)) ? 0.45 : 1, ...font }}>
                            {busyId === f.id ? '…' : f.waTagged
                              ? (isAr ? 'إلغاء الوسم' : 'Untag')
                              : (isAr ? 'وسم: سبق التواصل' : 'Tag: contacted')}
                          </button>
                        </div>
                      </td>
                      <td style={tdS}>
                        {f.ready
                          ? <span style={{ fontSize: 11, fontWeight: 600, color: '#2D6A4F', background: 'rgba(45,106,79,0.10)', border: '1px solid rgba(45,106,79,0.28)', borderRadius: 999, padding: '2px 9px' }}>{isAr ? 'جاهز' : 'Ready'}</span>
                          : <ReasonChip reason={f.reason} isAr={isAr} />}
                      </td>
                      <td style={tdS}>
                        <button className="ac-btn" style={{ padding: '4px 10px', ...font }} onClick={() => nav(`/admin2/suppliers/${f.id}`)} type="button">
                          {isAr ? 'تعديل' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
